import { Megaphone, Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { useLang } from "../i18n";

// I testi del post arrivano dallo Staff (Testi sito web -> pub.post_full / pub.post_short):
// quando lo Staff li cambia, questa pagina e l'anteprima del link si aggiornano da sole.
// I default di partenza sono nei dizionari i18n (IT/EN).

const BANNERS = [
  { label: "Banner server Discord 960x540", file: "banner_discord_server.png", note: "scritte in basso: il nome del server non copre nulla" },
  { label: "Discord HD 1920x1080", file: "banner_discord_server_hd.png", note: "stesso banner, nitidezza doppia" },
  { label: "Orizzontale 1920x640", file: "banner_pubblicita_wide.png", note: "Discord, forum, header" },
  { label: "Video 16:9 1920x1080", file: "banner_pubblicita_169.png", note: "video, schermi, presentazioni" },
  { label: "Post quadrato 1080x1080", file: "banner_pubblicita_square.png", note: "Instagram, Facebook" },
];

function copyText(text, what) {
  // primo tentativo: Clipboard API; se bloccata (browser aziendali), fallback
  // con textarea + execCommand; solo se fallisce anche quello, avviso manuale
  const legacy = () => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) toast.success(`${what} copiato — incollalo nel post`);
      else toast.error("Il browser ha bloccato la copia: seleziona il testo a mano");
    } catch {
      toast.error("Il browser ha bloccato la copia: seleziona il testo a mano");
    }
  };
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text)
      .then(() => toast.success(`${what} copiato — incollalo nel post`))
      .catch(legacy);
  } else {
    legacy();
  }
}

const PostCard = ({ title, desc, text, testid, limit }) => (
  <div className="chamfer border border-[#1A1A1A] hover:border-wardogs/40 bg-[#0C0C0C] p-6 transition-colors duration-300" data-testid={testid}>
    <div className="flex items-center gap-3 mb-3 flex-wrap">
      <Megaphone className="w-5 h-5 text-wardogs" />
      <h2 className="font-display text-2xl uppercase tracking-wide">{title}</h2>
      <span className="ml-auto font-mono text-[10px] text-steel" data-testid={`${testid}-count`}>
        {text.length} caratteri{limit ? ` / ${limit}` : ""}
      </span>
    </div>
    <p className="font-mono text-[11px] text-steel leading-relaxed mb-4">{desc}</p>
    <pre className="chamfer-sm border border-[#1A1A1A] bg-[#050505] p-4 font-mono text-[12px] text-[#C9C9CE] leading-relaxed whitespace-pre-wrap mb-4 select-text allow-copy" data-testid={`${testid}-text`}>{text}</pre>
    <button
      type="button"
      onClick={() => copyText(text, "Testo")}
      className="chamfer-sm bg-wardogs/10 border border-wardogs/40 hover:bg-wardogs hover:text-[#050505] text-wardogs font-mono text-[10px] uppercase tracking-[0.15em] font-bold px-4 py-2.5 transition-colors duration-300 flex items-center gap-2"
      data-testid={`${testid}-copy`}
    >
      <Copy className="w-3.5 h-3.5" /> Copia il testo
    </button>
  </div>
);

export default function Pubblicita() {
  const { t } = useLang();
  const postFull = t("pub.post_full");
  const postShort = t("pub.post_short");
  return (
    <main className="min-h-screen pt-32 pb-20" data-testid="pubblicita-page">
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <p className="hud-label mb-3">// kit pubblicità</p>
        <h1 className="font-display text-5xl uppercase mb-4" data-testid="pubblicita-title">Post social pronto</h1>
        <p className="font-mono text-xs text-steel leading-relaxed mb-10 max-w-2xl">
          Il testo pubblicitario ufficiale e i banner a tema italiano, pronti da copiare e pubblicare ovunque.
          Su Instagram e TikTok nessun sito può precompilare il post: copi il testo qui, scarichi il banner,
          apri l'app e incolli — dieci secondi ed è online.
        </p>

        <div className="flex flex-col gap-5 mb-12">
          <Reveal><PostCard testid="pubblicita-full" title="Post completo"
            desc="La versione lunga ufficiale: perfetta per Facebook, Discord, Telegram, forum e gruppi."
            text={postFull} /></Reveal>
          <Reveal><PostCard testid="pubblicita-short" title="Versione corta — X / Twitter"
            desc="Sta nei 280 caratteri di X. Va bene anche come didascalia rapida ovunque."
            text={postShort} limit={280} /></Reveal>
        </div>

        <p className="hud-label mb-3">// i banner da allegare</p>
        <h2 className="font-display text-3xl uppercase mb-6" data-testid="pubblicita-banners-title">Scegli il formato giusto</h2>
        <div className="flex flex-col gap-5 mb-8">
          {BANNERS.map((b) => (
            <Reveal key={b.file}>
              <div className="chamfer border border-[#1A1A1A] hover:border-wardogs/40 bg-[#0C0C0C] p-6 transition-colors duration-300" data-testid={`pubblicita-banner-${b.file.replace(/[^a-z0-9]+/gi, "-")}`}>
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <h3 className="font-display text-xl uppercase tracking-wide">{b.label}</h3>
                  <span className="font-mono text-[10px] text-steel">{b.note}</span>
                  <a
                    href={`/api/pubblicita/${b.file}`}
                    className="ml-auto chamfer-sm bg-wardogs/10 border border-wardogs/40 hover:bg-wardogs hover:text-[#050505] text-wardogs font-mono text-[10px] uppercase tracking-[0.15em] font-bold px-4 py-2.5 transition-colors duration-300 flex items-center gap-2"
                    data-testid={`pubblicita-download-${b.file.replace(/[^a-z0-9]+/gi, "-")}`}
                  >
                    <Download className="w-3.5 h-3.5" /> Scarica PNG
                  </a>
                </div>
                {/* anteprima inline via /api/panel (senza header di download), download via /api/pubblicita */}
                <img src={`/api/panel/${b.file}`} alt={b.label} loading="lazy"
                  className="w-full chamfer-sm border border-[#1A1A1A]" />
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <a href="/api/pubblicita/zip/tutti"
            className="chamfer-sm bg-wardogs/10 border border-wardogs/40 hover:bg-wardogs hover:text-[#050505] text-wardogs font-mono text-[10px] uppercase tracking-[0.15em] font-bold px-4 py-2.5 transition-colors duration-300 inline-flex items-center gap-2"
            data-testid="pubblicita-download-zip">
            <Download className="w-3.5 h-3.5" /> Scarica tutti e 3 in ZIP
          </a>
        </Reveal>
      </div>
    </main>
  );
}
